// Cent-exact event allocation helpers (moved unchanged from the expense form,
// components/accounting-expenses-form/config.ts, which re-exports them; also
// used by the stock transfer event split, T-L09 / owner rule L14).

export const calculateEventAmount = (amount: number, percent: number) =>
  Number(((amount * percent) / 100).toFixed(2));

// Allocate whole cents across all events together. Rounding each percentage
// independently makes a 50/50 split of an odd-cent charge exceed its payment.
export const calculateEventAmounts = (amount: number, percents: number[]) => {
  if (
    !Number.isFinite(amount) ||
    percents.some((percent) => !Number.isFinite(percent) || percent < 0) ||
    Math.abs(percents.reduce((sum, percent) => sum + percent, 0) - 100) > 1e-8
  ) {
    return percents.map((percent) => calculateEventAmount(amount, percent));
  }

  const sign = Math.sign(amount);
  const totalCents = Math.round(Math.abs(amount) * 100);
  const exactCents = percents.map((percent) => (totalCents * percent) / 100);
  const allocatedCents = exactCents.map(Math.floor);
  const remainder =
    totalCents - allocatedCents.reduce((sum, cents) => sum + cents, 0);
  const remainderOrder = exactCents
    .map((cents, index) => ({ index, fraction: cents - Math.floor(cents) }))
    .sort((a, b) => b.fraction - a.fraction || b.index - a.index);

  for (let index = 0; index < remainder; index += 1) {
    allocatedCents[remainderOrder[index].index] += 1;
  }

  return allocatedCents.map((cents) => (sign * cents) / 100);
};

export const calculateEventPercents = (
  amount: number,
  eventAmounts: number[]
): number[] | null => {
  const totalCents = Math.round(amount * 100);
  const eventCents = eventAmounts.map((value) => Math.round(value * 100));
  if (
    !Number.isFinite(totalCents) ||
    totalCents === 0 ||
    eventCents.some(
      (value) =>
        !Number.isFinite(value) || Math.sign(value) !== Math.sign(totalCents)
    ) ||
    eventCents.reduce((sum, value) => sum + value, 0) !== totalCents
  ) {
    return null;
  }

  const exactBasisPoints = eventCents.map(
    (value) => (Math.abs(value) * 10000) / Math.abs(totalCents)
  );
  const basisPoints = exactBasisPoints.map(Math.floor);
  const remainder = 10000 - basisPoints.reduce((sum, value) => sum + value, 0);
  const remainderOrder = exactBasisPoints
    .map((value, index) => ({ index, fraction: value - Math.floor(value) }))
    .sort((a, b) => b.fraction - a.fraction || b.index - a.index);

  for (let index = 0; index < remainder; index += 1) {
    basisPoints[remainderOrder[index].index] += 1;
  }

  return basisPoints.map((value) => value / 100);
};
